import { useEffect } from 'react';
import {
  exclusionCookie,
  sessionLifetime,
  trackedPaths,
  uuidPattern,
  visitorLifetime,
} from './shared';

type Identity = { id: string; updatedAt: number };

function readIdentity(key: string, lifetime: number, now: number): Identity {
  const saved = localStorage.getItem(key);
  if (saved) {
    try {
      const value = JSON.parse(saved);
      if (
        uuidPattern.test(value.id) &&
        Number.isFinite(value.updatedAt) &&
        now >= value.updatedAt &&
        now - value.updatedAt < lifetime
      ) {
        return value;
      }
    } catch {
      // Replace malformed or expired browser storage.
    }
  }
  return { id: crypto.randomUUID(), updatedAt: now };
}

export function trackPageView(path: string) {
  if (
    !trackedPaths.has(path) ||
    document.cookie
      .split(';')
      .some((part) => part.trim() === `${exclusionCookie}=1`) ||
    navigator.doNotTrack === '1' ||
    (navigator as Navigator & { globalPrivacyControl?: boolean })
      .globalPrivacyControl
  )
    return;

  try {
    const now = Date.now();
    const visitor = readIdentity('analytics_visitor', visitorLifetime, now);
    const session = readIdentity('analytics_session', sessionLifetime, now);
    localStorage.setItem('analytics_visitor', JSON.stringify(visitor));
    localStorage.setItem(
      'analytics_session',
      JSON.stringify({ ...session, updatedAt: now }),
    );
    let previous: { sessionId?: string; path?: string } | null = null;
    try {
      previous = JSON.parse(
        sessionStorage.getItem('analytics_previous') || 'null',
      );
    } catch {
      // A damaged navigation record should not prevent future page views.
    }
    const previousPath =
      previous?.sessionId === session.id &&
      typeof previous.path === 'string' &&
      trackedPaths.has(previous.path)
        ? previous.path
        : null;
    sessionStorage.setItem(
      'analytics_previous',
      JSON.stringify({ sessionId: session.id, path }),
    );

    void fetch('/api/analytics/events', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      keepalive: true,
      body: JSON.stringify({
        id: crypto.randomUUID(),
        visitorId: visitor.id,
        sessionId: session.id,
        path,
        previousPath,
      }),
    }).catch(() => {});
  } catch {
    // Analytics must never break navigation. Skip when storage is unavailable.
  }
}

export function usePageViews(path: string) {
  useEffect(() => {
    // Cancellation avoids StrictMode's extra effect and intermediate redirects.
    const timer = window.setTimeout(() => trackPageView(path), 0);
    return () => window.clearTimeout(timer);
  }, [path]);
}
